import React, { useRef, useState } from 'react';
import { IconCheck, IconTag, IconX } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import type { SmsTemplate } from '../../../entities/sms-template';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { applyPreview, EMPTY_SMS_TEMPLATE_FORM, SMS_VARIABLES, type SmsTemplateForm } from '../model/types';
import { SmsTemplateEditor } from './SmsTemplateEditor';

interface SmsTemplateModalProps {
  mode: 'add' | 'edit';
  initial?: SmsTemplate | null;
  onClose: () => void;
  onSaved: (updated?: SmsTemplate) => void;
}

const SmsTemplateModal: React.FC<SmsTemplateModalProps> = ({ mode, initial, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<SmsTemplateForm>({
    defaultValues: mode === 'edit' && initial
      ? { name: initial.name, text: initial.text }
      : EMPTY_SMS_TEMPLATE_FORM,
  });

  const textValue = watch('text');

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const payload = { name: values.name.trim(), text: values.text };
      if (mode === 'add') {
        const record = await pb.collection('sms_templates').create<SmsTemplate>(payload);
        logAction(`Created SMS template: ${values.name.trim()}`, 'ADD');
        onSaved(record);
      } else {
        const record = await pb.collection('sms_templates').update<SmsTemplate>(initial!.id, payload);
        logAction(`Edited SMS template: ${values.name.trim()}`, 'EDIT');
        onSaved(record);
      }
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 680 }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Добавить шаблон' : 'Редактировать шаблон'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>

        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 16 }}>
          <div className="adm-field">
            <label className="adm-label">
              Название <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconTag size={15} stroke={2} className="adm-input-icon" />
              <Input
                className="adm-input"
                type="text"
                placeholder="Название шаблона"
                autoComplete="off"
                {...register('name', { required: 'Введите название' })}
              />
            </div>
            {errors.name && <div className="form-field-error">{errors.name.message}</div>}
          </div>

          <div className="adm-field">
            <label className="adm-label" style={{ marginBottom: 8 }}>Текст</label>
            <SmsTemplateEditor
              defaultValue={textValue}
              onChange={v => setValue('text', v, { shouldValidate: false })}
              variables={SMS_VARIABLES}
            />
          </div>

          {textValue?.trim() && (
            <div className="adm-field">
              <label className="adm-label">Предпросмотр</label>
              <div style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.7,
                background: 'var(--color-bg-subtle, #f9fafb)',
                border: '1px solid var(--color-border, #e5e7eb)',
                whiteSpace: 'pre-wrap', color: 'var(--color-text, #111827)',
              }}>
                {applyPreview(textValue)}
              </div>
            </div>
          )}

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>Отмена</Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { SmsTemplateModal };
